/**
 * Consent Read V1 Service
 * Migrated from 3in1cms legacy/consentReadv1Api.ts
 */

import { request } from "../core/request";
import { API_RESPONSE_CODES, OPERATION_IDS } from "./constants";
import type { IConsentReadResponse, IConsentRequestV1 } from "../types/consentReadV1Types";
import type { IConsentReadV2Request, IConsentReadV2Response } from "../types/consentReadV2Types";

/**
 * Read consent for user
 * @param partnerName - Partner name
 * @param partnerId - Partner ID
 * @param cmfConsentCategory - CMF consent category
 * @param mobileNo - User mobile number
 * @returns Consent read response
 */
export const getReadConsent = async (
  partnerName: string,
  partnerId: string,
  cmfConsentCategory: string,
  mobileNo: string
): Promise<IConsentReadResponse> => {
  try {
    if (!mobileNo) {
      return {} as IConsentReadResponse;
    }

    const requestPayload: IConsentRequestV1 = {
      mobileNo,
      partnerName,
      partnerId,
      consentCategory: cmfConsentCategory,
    } as IConsentRequestV1;

    const consentResponse = (await request<IConsentReadResponse, IConsentRequestV1>({
      operationId: OPERATION_IDS.consentRead,
      requestData: requestPayload,
      additionalHeaders: {
        apiVersion: "v1",
      },
    })) as IConsentReadResponse;

    if (
      consentResponse.statusCode === API_RESPONSE_CODES.STATUS_CODE_7001 ||
      consentResponse.statusCode === API_RESPONSE_CODES.STATUS_CODE_7002 ||
      consentResponse.statusCode === API_RESPONSE_CODES.STATUS_CODE_7003
    ) {
      return consentResponse;
    }

    throw new Error(JSON.stringify(consentResponse));
  } catch (err) {
    console.error("Error in getReadConsent", err);
    throw err;
  }
};

/**
 * Read consent using v2 request/response contract.
 * Used by Solar form to evaluate consentResponseInfo for specific consent IDs.
 */
export const readConsentV2 = async (
  requestPayload: IConsentReadV2Request
): Promise<IConsentReadV2Response> => {
  try {
    if (!requestPayload?.mobileNo) {
      return {} as IConsentReadV2Response;
    }

    const consentResponse = (await request<IConsentReadV2Response, IConsentReadV2Request>({
      operationId: OPERATION_IDS.consentRead,
      requestData: requestPayload,
      additionalHeaders: {
        apiVersion: "v2",
      },
    })) as IConsentReadV2Response;

    if (
      consentResponse.statusCode === API_RESPONSE_CODES.STATUS_CODE_7001 ||
      consentResponse.statusCode === API_RESPONSE_CODES.STATUS_CODE_7002 ||
      consentResponse.statusCode === API_RESPONSE_CODES.STATUS_CODE_7003
    ) {
      return consentResponse;
    }

    throw new Error(JSON.stringify(consentResponse));
  } catch (err) {
    console.error("Error in readConsentV2", err);
    throw err;
  }
};
